import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const ServicesSection = () => {
  const [showAllMobile, setShowAllMobile] = useState(false);
  const services = [
    {
      icon: 'ri-scales-3-line',
      title: 'Tramitación de Posesión Efectiva',
      description: 'Servicio completo para gestionar la posesión efectiva de una herencia en Chile. Nos encargamos de toda la tramitación legal y administrativa: recopilación de antecedentes, presentación de solicitud, seguimiento del expediente y orientación posterior.',
      badge: 'Profesional',
    },
    {
      icon: 'ri-file-edit-line',
      title: 'Redacción de contratos',
      description: 'Redacción profesional de contratos para empresas y emprendedores como contratos de prestación de servicios, contratos freelance, acuerdos comerciales, acuerdos de confidencialidad (NDA) y contratos de colaboración empresarial.',
      badge: 'IA + Abogado',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Revisión de contratos',
      description: 'Análisis legal de contratos antes de firmar para identificar riesgos legales, cláusulas problemáticas y posibles mejoras. El análisis combina herramientas tecnológicas y revisión profesional.',
      badge: 'IA + Abogado',
    },
    {
      icon: 'ri-building-line',
      title: 'Constitución de empresas SpA',
      description: 'Servicio completo para crear empresas en Chile, incluyendo redacción de escritura de constitución, definición de objeto social y asesoría básica para emprendedores.',
      badge: 'Profesional',
    },
    {
      icon: 'ri-trademark-line',
      title: 'Registro de marca',
      description: 'Gestión de registro de marca ante el Instituto Nacional de Propiedad Industrial en Chile, incluyendo búsqueda preliminar de disponibilidad, presentación de solicitud y seguimiento del proceso.',
      badge: 'Profesional',
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Modificación y gestión de sociedades',
      description: 'Gestión completa de modificaciones societarias: cambio de razón social, modificación de estatutos, ingreso o salida de socios, aumento o disminución de capital. Incluye trámites tributarios ante el SII como inicio de actividades y acreditación de giro, con coordinación notarial cuando corresponde.',
      badge: 'Profesional',
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Gestiones notariales',
      description: 'Servicios de preparación de documentos para trámites notariales, incluyendo redacción de poderes especiales, preparación de escrituras y coordinación para autorización de documentos.',
      badge: 'Profesional',
    },
  ];

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #FFD700 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Servicios principales
          </h2>
          <div className="w-20 h-1 bg-primary-yellow"></div>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden', willChange: 'transform, opacity' }}
              className={`bg-white rounded-2xl p-5 relative group hover:shadow-2xl hover:shadow-yellow-500/20 transition-shadow transition-transform duration-300 cursor-pointer${index >= 3 ? ' hidden md:block' : ''}${index >= 3 && showAllMobile ? ' !block' : ''}`}
            >
              {/* Badge */}
              <div className="absolute top-3 right-3 bg-primary-yellow text-black text-xs font-bold px-2 py-0.5 rounded-full">
                {service.badge}
              </div>

              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center bg-primary-yellow rounded-full mb-3">
                <i className={`${service.icon} text-xl text-black`}></i>
              </div>

              {/* Content */}
              <h3 className="text-base font-bold text-black mb-2 group-hover:text-gray-800 transition-colors pr-16">
                {service.title}
              </h3>
              <p className="text-gray-dark text-sm leading-relaxed">
                {service.description}
              </p>

              {/* Hover Effect Border */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary-yellow rounded-2xl transition-all duration-300"></div>
            </motion.div>
          ))}
        </div>

        {/* Mobile "Ver más" button */}
        {!showAllMobile && (
          <div className="flex justify-center mt-6 md:hidden">
            <button
              onClick={() => setShowAllMobile(true)}
              className="flex items-center space-x-2 border-2 border-primary-yellow text-primary-yellow px-8 py-3 rounded-full font-bold hover:bg-primary-yellow hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span>Ver más servicios</span>
              <i className="ri-arrow-down-s-line text-xl"></i>
            </button>
          </div>
        )}

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center mt-16"
        >
          <Link
            to="/servicios"
            className="inline-block bg-primary-yellow text-black px-10 py-4 rounded-full font-bold hover:bg-yellow-400 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/50 whitespace-nowrap cursor-pointer"
          >
            Ver todos los servicios
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;